import { Language, LanguageMarshaller } from 'app/constants/enums/Language';
import { DateMarshaller } from 'app/constants/schemas/Date';
import { IImage, Image } from 'app/constants/schemas/Image';
import { IIssue, Issue } from 'app/constants/schemas/Issue';
import { ISentenceTranslation, SentenceTranslation } from 'app/constants/schemas/SentenceTranslation';
import { ISound, Sound } from 'app/constants/schemas/Sound';
import { TranslationsForEntity, ALL_TRANSLATIONS } from 'app/constants/schemas/translations';
import vmClient from 'app/services/vmClient';
import { Entity, EntityStringId, IEntity, IEntityType } from 'app/types/entity';

export interface ISentence extends IEntity {
  attributes: {
    id: EntityStringId;
    lock_version: number;
    language: Language;
    text: string;
    transliteration: string | null;
    internalTags: string[];
    draft: boolean;
    textUpdatedAt: Date;
    createdAt: Date;
    updatedAt: Date;
  };
  associations: {
    image: IImage | null;
    sound: ISound | null;
    translations: ISentenceTranslation[];
    issues: IIssue[];
  };
}

export type Sentence = Entity<ISentence>;
// defineClass is a class factory, normally classes create a runtime variable and identifier in the type namespace.
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const Sentence: IEntityType<ISentence> = vmClient.defineClass({
  typeName: 'Sentence',
  version: 3,
  lockable: true,
  persisted: true,
  attributes: {
    language: {
      type: Language,
      marshaller: LanguageMarshaller,
      notNull: true,
    },
    text: {
      type: 'string',
      notNull: true,
    },
    transliteration: 'string',
    internalTags: {
      type: 'string',
      collection: true,
      notNull: true,
    },
    draft: {
      type: 'boolean',
      notNull: true,
    },
    textUpdatedAt: {
      type: Date,
      marshaller: DateMarshaller,
      notNull: true,
    },
    createdAt: {
      type: Date,
      marshaller: DateMarshaller,
      notNull: true,
    },
    updatedAt: {
      type: Date,
      marshaller: DateMarshaller,
      notNull: true,
    },
  },
  associations: {
    image: Image,
    sound: Sound,
    translations: {
      type: SentenceTranslation,
      collection: true,
      notNull: true,
    },
    issues: {
      type: Issue,
      collection: true,
      notNull: true,
    },
  },
});

export const SentenceFieldTranslations: Partial<TranslationsForEntity<ISentence>> = {
  id: {
    getLabel: (t) => t('entity-fields.Sentence.id.label'),
  },
  text: {
    getLabel: (t) => t('entity-fields.Sentence.text.label'),
    getInlineLabel: (t) => t('entity-fields.Sentence.text.inline-label'),
  },
  transliteration: {
    getLabel: (t) => t('entity-fields.Sentence.transliteration.label'),
    getInlineLabel: (t) => t('entity-fields.Sentence.transliteration.inline-label'),
  },
  internalTags: {
    getLabel: (t) => t('entity-fields.Sentence.internalTags.label'),
  },
  translations: {
    getLabel: (t) => t('entity-fields.Sentence.translations.label'),
  },
  language: {
    getLabel: (t) => t('entity-fields.Sentence.language.label'),
  },
  draft: {
    getLabel: () => '',
    getInlineLabel: (t) => t('entity-fields.Sentence.draft.inline-label'),
  },
};

ALL_TRANSLATIONS.set(Sentence, SentenceFieldTranslations);
